// library imports
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import type { MediaAssetResponse, FamilyUsersResponse } from 'we-tree-api-client/v2';
import type { FamilyUserSummary } from '@/types/family-user';

// store imports
import { useDomainStore, useFamilyStore, useReviewQueueStore, useSuggestionsStore } from '@/hooks/useStores';

// component imports
import ClaimProfileModal from '@/components/modals/ClaimProfileModal';
import ClaimProfileBanner from '@/components/modals/ClaimProfileBanner';
import EditFamilyNameModal from '@/components/family/EditFamilyNameModal';
import FamilyUserDetailsModal from '@/components/family/FamilyUserDetailsModal';
import Spinner from '@/components/shared/Spinner';
import FamilyPageHeader from '@/components/layout/FamilyPageHeader';
import OverviewStatsGrid from '@/components/family/overview/OverviewStatsGrid';
import OverviewPendingInvitesCard from '@/components/family/overview/OverviewPendingInvitesCard';
import OverviewPendingReviewCard from '@/components/family/overview/OverviewPendingReviewCard';
import OverviewPendingSuggestionsCard from '@/components/family/overview/OverviewPendingSuggestionsCard';
import OverviewLatestPhotosCard from '@/components/family/overview/OverviewLatestPhotosCard';
import OverviewRecentActivityCard from '@/components/family/overview/OverviewRecentActivityCard';
import OverviewUsersByRoleCard from '@/components/family/overview/OverviewUsersByRoleCard';
import OverviewEmptyHero from '@/components/family/overview/OverviewEmptyHero';
import OverviewMetaFooter from '@/components/family/overview/OverviewMetaFooter';
import OverviewPlanCard from '@/components/family/overview/OverviewPlanCard';
import UpgradeFamilyPlanModal, { type UpgradeModalPhase } from '@/components/family/UpgradeFamilyPlanModal';
import OneTimePaymentModal from '@/components/family/OneTimePaymentModal';
import BillingHistoryModal from '@/components/family/BillingHistoryModal';
import ExportMediaModal from '@/components/media/ExportMediaModal';
import ExportGedcomModal from '@/components/media/ExportGedcomModal';

// api
import { api } from '@/api';
import { mediaApi } from '@/api/mediaWrapper';
import { sourcesApi } from '@/api/sourcesWrapper';
import { storiesApi } from '@/api/storiesWrapper';

// utils
import {
  canExportGedcom,
  canExportMedia,
  canInvite,
  canUploadGedcom,
  canViewUserDetails
} from '@/utils/rolePermissions';
import { canAccessReviewQueue } from '@/types/suggestion';
import { canHaveAuthorSuggestions } from '@/stores/SuggestionsStore';
import { hasBillingActivating } from '@/utils/billingActivation';
import i18n from '@/i18n/i18n';

function formatPendingQueuesEmptyMessage(emptyLabels: string[]): string | null {
  if (emptyLabels.length === 0) return null;
  if (emptyLabels.length === 1) {
    return i18n.t('overview:pendingQueuesEmpty.single', { item: emptyLabels[0] });
  }
  if (emptyLabels.length === 2) {
    return i18n.t('overview:pendingQueuesEmpty.two', { first: emptyLabels[0], second: emptyLabels[1] });
  }
  const head = emptyLabels.slice(0, -1).join(', ');
  return i18n.t('overview:pendingQueuesEmpty.many', { items: head, last: emptyLabels[emptyLabels.length - 1] });
}

// styles
import './family-overview.css';
import ScreenHelpDot from '@/components/help/ScreenHelpDot';
import ScreenTourLayer from '@/components/help/ScreenTourLayer';
import { useScreenTour } from '@/hooks/useScreenTour';
import {
  FAMILY_OVERVIEW_TOUR_STEPS,
  FAMILY_OVERVIEW_TOUR_STORAGE_KEY,
  resolveFamilyOverviewTourSteps
} from '@/data/family-overview-tour';

const FamilyOverviewScreen: React.FC = observer(() => {
  const { t } = useTranslation('screens');
  const { t: tForms } = useTranslation('forms');
  const domainStore = useDomainStore();
  const familyStore = useFamilyStore();
  const reviewQueueStore = useReviewQueueStore();
  const suggestionsStore = useSuggestionsStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { familyId: familyIdParam } = useParams<{
    familyId: string;
  }>();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pendingInviteCount, setPendingInviteCount] = useState<number | null>(null);
  const [showClaimModal, setShowClaimModal] = useState<boolean>(false);
  const [showEditNameModal, setShowEditNameModal] = useState<boolean>(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState<boolean>(false);
  const [upgradeModalPhase, setUpgradeModalPhase] = useState<UpgradeModalPhase>('choose');
  const [showContributeModal, setShowContributeModal] = useState(false);
  const [showBillingHistoryModal, setShowBillingHistoryModal] = useState(false);
  const [showExportMediaModal, setShowExportMediaModal] = useState(false);
  const [showExportGedcomModal, setShowExportGedcomModal] = useState(false);
  const [profileClaimed, setProfileClaimed] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [userCounts, setUserCounts] = useState<{
    total: number;
    owner: number;
    admin: number;
    editor: number;
    contributor: number;
    viewer: number;
  } | null>(null);
  const [users, setUsers] = useState<FamilyUserSummary[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);
  const [userDetailsModalRole, setUserDetailsModalRole] = useState<string | null>(null);
  const [photoCount, setPhotoCount] = useState<number | null>(null);
  const [memoryCount, setMemoryCount] = useState<number | null>(null);
  const [sourceCount, setSourceCount] = useState<number | null>(null);
  const [latestPhotos, setLatestPhotos] = useState<MediaAssetResponse[]>([]);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);

  const overviewMemberCount =
    domainStore.currentFamily?.memberCount ?? (familyStore.isLoaded ? familyStore.people.size : 0);
  const overviewTour = useScreenTour({
    steps: FAMILY_OVERVIEW_TOUR_STEPS,
    completedStorageKey: FAMILY_OVERVIEW_TOUR_STORAGE_KEY,
    resolveSteps: (steps) =>
      resolveFamilyOverviewTourSteps(
        steps,
        overviewMemberCount > 0,
        Boolean(domainStore.currentFamily?.userRole),
        canExportGedcom(domainStore.currentFamily?.userRole),
        canInvite(domainStore.currentFamily?.userRole, domainStore.currentFamily?.plan)
      )
  });

  useEffect(() => {
    const loadFamilyData = async () => {
      setErrorMessage(null);

      if (!familyIdParam) {
        setErrorMessage(t('errors.noFamily'));
        return;
      }

      try {
        setPendingInviteCount(null);
        domainStore.setSelectedFamily(familyIdParam);
        if (!familyStore.isLoaded || familyStore.familyId !== familyIdParam) {
          await familyStore.loadFamilyData(familyIdParam);
        }
        setProfileClaimed(false);

        setIsLoadingUsers(true);
        const usersResult = await api.families.getFamilyUsers(familyIdParam);
        if (usersResult.success && usersResult.data) {
          const body = usersResult.data as FamilyUsersResponse & { data?: FamilyUsersResponse };
          const data = body?.data ?? body;
          const counts = data.countsByRole ?? { owner: 0, admin: 0, editor: 0, contributor: 0, viewer: 0 };
          setUserCounts({
            total: data.total ?? 0,
            owner: counts.owner ?? 0,
            admin: counts.admin ?? 0,
            editor: counts.editor ?? 0,
            contributor: counts.contributor ?? 0,
            viewer: counts.viewer ?? 0
          });
          setUsers(Array.isArray(data.users) ? data.users : []);
        } else if (!usersResult.success) {
          // eslint-disable-next-line no-console
          console.warn('Failed to load family users:', usersResult.error.getUserMessage());
        }
        setIsLoadingUsers(false);

        setIsLoadingPhotos(true);
        const [photosResult, memoriesResult, sourcesResult] = await Promise.all([
          mediaApi.searchMediaAssets(familyIdParam, { mimeType: 'image', page: 1, pageSize: 6 }),
          storiesApi.searchStories(familyIdParam, { page: 1, pageSize: 1 }),
          sourcesApi.listSources(familyIdParam, { page: 1, pageSize: 1 })
        ]);
        if (photosResult.success && photosResult.data) {
          setPhotoCount(typeof photosResult.data.total === 'number' ? photosResult.data.total : 0);
          setLatestPhotos(Array.isArray(photosResult.data.items) ? photosResult.data.items : []);
        } else {
          setPhotoCount(null);
          setLatestPhotos([]);
        }
        if (memoriesResult.success && memoriesResult.data) {
          setMemoryCount(typeof memoriesResult.data.total === 'number' ? memoriesResult.data.total : 0);
        } else {
          setMemoryCount(null);
        }
        if (sourcesResult.success && sourcesResult.data) {
          setSourceCount(typeof sourcesResult.data.total === 'number' ? sourcesResult.data.total : 0);
        } else {
          setSourceCount(null);
        }
        setIsLoadingPhotos(false);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load family data:', error);
        setErrorMessage(t('overview.loadFailed'));
        setIsLoadingUsers(false);
        setIsLoadingPhotos(false);
      }
    };

    loadFamilyData();
  }, [domainStore, familyStore, familyIdParam, t]);

  // Survive Paddle “Return to …” navigation: reopen activating UX from query or sessionStorage.
  useEffect(() => {
    if (!familyIdParam) return;
    const fromQuery = searchParams.get('billing') === 'activating';
    const fromStorage = hasBillingActivating(familyIdParam);
    if (!fromQuery && !fromStorage) return;

    setUpgradeModalPhase('activating');
    setShowUpgradeModal(true);

    if (fromQuery) {
      const next = new URLSearchParams(searchParams);
      next.delete('billing');
      setSearchParams(next, { replace: true });
    }
  }, [familyIdParam, searchParams, setSearchParams]);

  const hasClaimedProfile = (() => {
    if (profileClaimed) return true;
    if (!domainStore.user?.id) return false;
    if (familyStore.people.size === 0) return false;
    const userId = domainStore.user.id;
    for (const person of familyStore.people.values()) {
      if (person.userId === userId) {
        return true;
      }
    }
    return false;
  })();

  const family =
    familyStore.familyId === familyIdParam && familyStore.isLoaded && domainStore.currentFamily
      ? {
          name: domainStore.currentFamily.name,
          displayName: domainStore.currentFamily.displayName || domainStore.currentFamily.name,
          alternativeName: domainStore.currentFamily.alternativeName ?? null,
          plan: domainStore.currentFamily.plan || 'free',
          memberCount: domainStore.currentFamily.memberCount,
          createdAt: domainStore.currentFamily.createdAt,
          createdBy: domainStore.currentFamily.createdBy,
          createdByNickname: domainStore.currentFamily.createdByNickname
        }
      : null;

  const userRole = domainStore.currentFamily?.userRole;
  const canInviteMembers = canInvite(userRole, domainStore.currentFamily?.plan);
  const canUploadGedcomFile = canUploadGedcom(userRole);
  const canViewDetails = canViewUserDetails(userRole);
  const isOwner = userRole === 'owner';
  const showPlanCard = Boolean(userRole);
  const canExportLibrary = canExportMedia(userRole);
  const canExportGedcomFile = canExportGedcom(userRole);
  const canReviewQueue = canAccessReviewQueue(userRole);
  const canAuthorSuggestions = canHaveAuthorSuggestions(userRole);
  const hasMembers = (family?.memberCount ?? 0) > 0;

  const reviewPendingCount =
    familyIdParam && reviewQueueStore.pendingSummaryFamilyId === familyIdParam ? reviewQueueStore.pendingTotal : 0;
  const suggestionPendingCount =
    familyIdParam && suggestionsStore.pendingSummaryFamilyId === familyIdParam
      ? suggestionsStore.pendingAuthorTotal
      : 0;
  const queuesSettled =
    (!canReviewQueue || !reviewQueueStore.isLoadingPendingSummary) &&
    (!canAuthorSuggestions || !suggestionsStore.isLoadingPendingSummary) &&
    (!canInviteMembers || pendingInviteCount !== null);

  const emptyQueueLabels: string[] = [];
  if (canReviewQueue && reviewPendingCount === 0) emptyQueueLabels.push(i18n.t('overview:queueLabels.reviews'));
  if (canAuthorSuggestions && suggestionPendingCount === 0) {
    emptyQueueLabels.push(i18n.t('overview:queueLabels.suggestions'));
  }
  if (canInviteMembers && (pendingInviteCount ?? 0) === 0) {
    emptyQueueLabels.push(i18n.t('overview:queueLabels.invites'));
  }
  const pendingQueuesEmptyMessage =
    queuesSettled && emptyQueueLabels.length > 0 ? formatPendingQueuesEmptyMessage(emptyQueueLabels) : null;

  const handleClaimProfile = () => setShowClaimModal(true);

  const handleClaimSuccess = async (claimedPersonId?: string) => {
    setShowClaimModal(false);
    setProfileClaimed(true);

    if (claimedPersonId && domainStore.user) {
      domainStore.setUser({
        id: domainStore.user.id,
        personId: claimedPersonId,
        email: domainStore.user.email,
        nickname: domainStore.user.nickname,
        createdAt: domainStore.user.createdAt
      });
    }

    if (familyIdParam) {
      try {
        await familyStore.loadFamilyData(familyIdParam);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to reload family data after claim:', error);
      }
    }
  };

  const handleUploadGedcom = () => {
    if (!familyIdParam) return;
    navigate(`/families/${familyIdParam}/upload`);
  };

  const handleInviteMembers = () => {
    if (!familyIdParam) return;
    navigate(`/families/${familyIdParam}/invite`);
  };

  const handleViewTree = () => {
    if (!familyIdParam) return;
    navigate(`/families/${familyIdParam}/hybrid-pedigree/user`);
  };

  const handleViewMedia = () => {
    if (!familyIdParam) return;
    navigate(`/families/${familyIdParam}/media`);
  };

  const handleViewSources = () => {
    if (!familyIdParam) return;
    navigate(`/families/${familyIdParam}/sources`);
  };

  const handleViewActivity = () => {
    if (!familyIdParam) return;
    navigate(`/families/${familyIdParam}/activity`);
  };

  const handleViewReview = () => {
    if (!familyIdParam) return;
    navigate(`/families/${familyIdParam}/review`);
  };

  const handleViewSuggestions = () => {
    if (!familyIdParam) return;
    navigate(`/families/${familyIdParam}/suggestions`);
  };

  const handleViewMemories = () => {
    if (!familyIdParam) return;
    navigate(`/families/${familyIdParam}/memories`);
  };

  const handleInviteSummaryLoaded = (count: number) => {
    setPendingInviteCount(count);
  };

  const handleRoleCountClick = (role: string) => {
    if (canViewDetails) setUserDetailsModalRole(role);
  };

  const handleEditNameSuccess = (displayName: string, alternativeName: string | null) => {
    if (!familyIdParam || !domainStore.currentFamily) return;
    domainStore.updateFamilyMetadata({
      id: familyIdParam,
      name: domainStore.currentFamily.name,
      displayName,
      alternativeName,
      memberCount: domainStore.currentFamily.memberCount,
      createdAt: domainStore.currentFamily.createdAt,
      updatedAt: domainStore.currentFamily.updatedAt,
      createdBy: domainStore.currentFamily.createdBy ?? null,
      createdByNickname: domainStore.currentFamily.createdByNickname ?? null,
      updatedBy: domainStore.currentFamily.updatedBy ?? null,
      updatedByNickname: domainStore.currentFamily.updatedByNickname ?? null,
      userRole: domainStore.currentFamily.userRole ?? null
    });
  };

  const handleAddSelf = () => {
    if (!familyIdParam) return;
    navigate(`/families/${familyIdParam}/people/add?addSelf=true`, {
      state: { returnTo: `${location.pathname}${location.search}` }
    });
  };

  if (errorMessage) {
    return (
      <div className="overview-page">
        <div className="overview-error" role="alert">
          <h3 className="overview-error__title">{errorMessage}</h3>
          <button
            type="button"
            onClick={() => setErrorMessage(null)}
            className="overview-error__dismiss"
            aria-label={tForms('actions.dismiss')}
          >
            ×
          </button>
        </div>
      </div>
    );
  }

  const isLoading = !familyStore.isLoaded && familyStore.isLoading;

  if (isLoading) {
    return (
      <div className="overview-page overview-loading">
        <Spinner message={t('overview.loading')} />
      </div>
    );
  }

  if (!family) {
    return (
      <div className="overview-page">
        <div className="overview-not-found">
          <h3 className="overview-not-found__title">{t('overview.notFound')}</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="overview-page">
      <FamilyPageHeader
        titleTourId="overview-title"
        familyDisplayName={family.displayName}
        sectionTitle={t('overview.title')}
        onEditFamilyName={() => setShowEditNameModal(true)}
        onStartTour={overviewTour.startTour}
        intro={
          <>
            {t('overview.lede')} <ScreenHelpDot />
          </>
        }
        introTrailing={
          canInviteMembers ? (
            <button
              type="button"
              className="btn btn--primary"
              data-tour="overview-invite"
              onClick={handleInviteMembers}
            >
              {t('overview.invite')}
            </button>
          ) : undefined
        }
      />

      {!hasClaimedProfile && familyIdParam && hasMembers && <ClaimProfileBanner onClaimClick={handleClaimProfile} />}

      {!hasMembers ? (
        <OverviewEmptyHero
          isOwner={isOwner}
          canUploadGedcom={canUploadGedcomFile}
          onAddSelf={handleAddSelf}
          onUploadGedcom={handleUploadGedcom}
        />
      ) : (
        <>
          <div data-tour="overview-stats">
            <OverviewStatsGrid
              stats={{
                memberCount: family.memberCount,
                memoryCount,
                photoCount,
                sourceCount,
                reviewCount: canReviewQueue ? reviewPendingCount : null,
                suggestionCount: canAuthorSuggestions ? suggestionPendingCount : null
              }}
              onMembers={handleViewTree}
              onMemories={handleViewMemories}
              onPhotos={handleViewMedia}
              onSources={handleViewSources}
              onReview={canReviewQueue ? handleViewReview : undefined}
              onSuggestions={canAuthorSuggestions ? handleViewSuggestions : undefined}
            />
            {showPlanCard ? (
              <OverviewPlanCard
                plan={family.plan || 'free'}
                isOwner={isOwner}
                canExportGedcom={canExportGedcomFile}
                onExportGedcomClick={
                  canExportGedcomFile && familyIdParam
                    ? () => {
                        setShowExportGedcomModal(true);
                      }
                    : undefined
                }
                canExportMedia={canExportLibrary}
                onExportMediaClick={
                  canExportLibrary && familyIdParam
                    ? () => {
                        setShowExportMediaModal(true);
                      }
                    : undefined
                }
                onContributeClick={
                  !isOwner && familyIdParam
                    ? () => {
                        setShowContributeModal(true);
                      }
                    : undefined
                }
                onUpgradeClick={
                  isOwner
                    ? () => {
                        setUpgradeModalPhase('choose');
                        setShowUpgradeModal(true);
                      }
                    : undefined
                }
                onManageBillingClick={
                  isOwner && familyIdParam
                    ? () => {
                        const familyId = familyIdParam;
                        void (async () => {
                          try {
                            const url = await domainStore.prepareBillingPortal(familyId);
                            window.location.assign(url);
                          } catch (err) {
                            // eslint-disable-next-line no-console
                            console.error('Failed to open billing portal', err);
                            window.alert(err instanceof Error ? err.message : t('overview.billingPortalFailed'));
                          }
                        })();
                      }
                    : undefined
                }
                onBillingHistoryClick={
                  isOwner && familyIdParam
                    ? () => {
                        setShowBillingHistoryModal(true);
                      }
                    : undefined
                }
              />
            ) : null}
          </div>

          <div className="overview-dashboard" data-tour="overview-dashboard">
            {familyIdParam && <OverviewRecentActivityCard familyId={familyIdParam} onViewAll={handleViewActivity} />}
            {familyIdParam && (
              <OverviewLatestPhotosCard
                familyId={familyIdParam}
                photos={latestPhotos}
                totalCount={photoCount}
                isLoading={isLoadingPhotos}
                onViewAll={handleViewMedia}
                onPhotoClick={handleViewMedia}
              />
            )}
          </div>

          <div className="overview-dashboard">
            {userCounts && !isLoadingUsers && (
              <OverviewUsersByRoleCard
                counts={{
                  owner: userCounts.owner,
                  admin: userCounts.admin,
                  editor: userCounts.editor,
                  contributor: userCounts.contributor,
                  viewer: userCounts.viewer
                }}
                total={userCounts.total}
                canViewDetails={canViewDetails}
                onRoleClick={handleRoleCountClick}
              />
            )}
            {canInviteMembers && familyIdParam && (
              <OverviewPendingInvitesCard
                familyId={familyIdParam}
                canManage={canInviteMembers}
                onViewAll={handleInviteMembers}
                onSummaryLoaded={handleInviteSummaryLoaded}
              />
            )}
            {canReviewQueue && familyIdParam && (
              <OverviewPendingReviewCard
                familyId={familyIdParam}
                canReview={canReviewQueue}
                onViewAll={handleViewReview}
              />
            )}
            {canAuthorSuggestions && familyIdParam && (
              <OverviewPendingSuggestionsCard
                familyId={familyIdParam}
                canShow={canAuthorSuggestions}
                onViewAll={handleViewSuggestions}
              />
            )}
          </div>

          {pendingQueuesEmptyMessage ? (
            <p className="overview-queues-empty" role="status">
              {pendingQueuesEmptyMessage}
            </p>
          ) : null}

          <OverviewMetaFooter
            createdByNickname={family.createdByNickname ?? null}
            createdBy={family.createdBy ?? null}
            createdAt={family.createdAt}
          />
        </>
      )}

      {showClaimModal && familyIdParam && (
        <ClaimProfileModal
          familyId={familyIdParam}
          isOpen={showClaimModal}
          onClose={() => setShowClaimModal(false)}
          onClaimSuccess={handleClaimSuccess}
        />
      )}

      {showEditNameModal && familyIdParam && (
        <EditFamilyNameModal
          familyId={familyIdParam}
          currentDisplayName={family.displayName}
          canonicalName={family.name}
          hasAlternative={!!family.alternativeName}
          isOpen={showEditNameModal}
          onClose={() => setShowEditNameModal(false)}
          onSuccess={handleEditNameSuccess}
        />
      )}

      {showUpgradeModal && familyIdParam && (
        <UpgradeFamilyPlanModal
          familyId={familyIdParam}
          isOpen={showUpgradeModal}
          initialPhase={upgradeModalPhase}
          onClose={() => {
            setShowUpgradeModal(false);
            setUpgradeModalPhase('choose');
          }}
        />
      )}

      {showContributeModal && familyIdParam ? (
        <OneTimePaymentModal
          kind="contribute"
          familyId={familyIdParam}
          isOpen={showContributeModal}
          onClose={() => setShowContributeModal(false)}
        />
      ) : null}

      {showBillingHistoryModal && familyIdParam ? (
        <BillingHistoryModal
          familyId={familyIdParam}
          isOpen={showBillingHistoryModal}
          onClose={() => setShowBillingHistoryModal(false)}
        />
      ) : null}

      {showExportMediaModal && familyIdParam ? (
        <ExportMediaModal
          familyId={familyIdParam}
          isOpen={showExportMediaModal}
          onClose={() => setShowExportMediaModal(false)}
        />
      ) : null}

      {showExportGedcomModal && familyIdParam ? (
        <ExportGedcomModal
          familyId={familyIdParam}
          isOpen={showExportGedcomModal}
          onClose={() => setShowExportGedcomModal(false)}
        />
      ) : null}

      {userDetailsModalRole && userCounts && (
        <FamilyUserDetailsModal
          isOpen={!!userDetailsModalRole}
          onClose={() => setUserDetailsModalRole(null)}
          role={userDetailsModalRole}
          countForRole={userCounts[userDetailsModalRole as keyof typeof userCounts] ?? 0}
          canViewEmails={canViewDetails}
          users={users.filter((u) => u.role?.toLowerCase() === userDetailsModalRole?.toLowerCase())}
        />
      )}

      <ScreenTourLayer tour={overviewTour} />
    </div>
  );
});

export default FamilyOverviewScreen;
